import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  useTheme,
  CircularProgress,
  TextField,
  Box,
  FormHelperText,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { getMetrics } from 'services/MetricsService';
import { IMetricsResponse } from 'types/Metrics';
import InfoIcon from '@material-ui/icons/Info';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(4),
    backgroundColor: '#ffffff',
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 16,
    background: '#ffffff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease-in-out',
    border: '1px solid rgba(0, 0, 0, 0.03)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.08)',
    },
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  title: {
    color: '#02CBF4',
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1.5),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  value: {
    fontSize: '2rem',
    fontWeight: 600,
    color: '#1e293b',
    lineHeight: 1.2,
  },
  kpiValue: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '48px',
  },
  kpiLoading: {
    color: '#02CBF4',
  },
  kpiGrid: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
  },
  chartContainer: {
    height: 300,
    marginTop: theme.spacing(2),
    position: 'relative',
  },
  chartTitle: {
    color: '#02CBF4',
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  kpiCard: {
    '&:nth-child(1)': {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    },
    '&:nth-child(2)': {
      background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
    },
    '&:nth-child(3)': {
      background: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)',
    },
    '&:nth-child(4)': {
      background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
    },
    '&:nth-child(5)': {
      background: 'linear-gradient(135deg, #ffffff 0%, #fef3c7 100%)',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 300,
  },
  errorMessage: {
    color: '#E74C3C',
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  dateFilterContainer: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    backgroundColor: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    border: '1px solid rgba(0, 0, 0, 0.03)',
  },
  dateFilter: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
  },
  dateField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: '#f8fafc',
      '&:hover': {
        backgroundColor: '#f1f5f9',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.1)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.2)',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#02CBF4',
      borderWidth: 1,
    },
  },
  filterTitle: {
    color: '#02CBF4',
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  errorText: {
    color: '#E74C3C',
    marginTop: theme.spacing(1),
    fontSize: '0.75rem',
  },
  filterButton: {
    backgroundColor: '#02CBF4',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#02a8d4',
    },
    borderRadius: 8,
    padding: '8px 24px',
    textTransform: 'none',
    fontWeight: 600,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    border: '1px solid rgba(0, 0, 0, 0.03)',
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: '#f8fafc',
  },
  tableHeaderCell: {
    color: '#02CBF4',
    fontWeight: 600,
    fontSize: '0.875rem',
    borderBottom: '2px solid rgba(0, 0, 0, 0.03)',
  },
  tableCell: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.03)',
    padding: theme.spacing(2),
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
  },
  rankCell: {
    width: '50px',
    textAlign: 'center',
  },
  rankBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: '50%',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  goldBadge: {
    backgroundColor: '#FFD700',
    color: '#ffffff',
  },
  silverBadge: {
    backgroundColor: '#C0C0C0',
    color: '#ffffff',
  },
  bronzeBadge: {
    backgroundColor: '#CD7F32',
    color: '#ffffff',
  },
  defaultBadge: {
    backgroundColor: '#f1f5f9',
    color: '#64748b',
  },
  successRateCell: {
    fontWeight: 600,
    '&.high': {
      color: '#10B981', // Verde para tasas altas
    },
    '&.medium': {
      color: '#F59E0B', // Naranja para tasas medias
    },
    '&.low': {
      color: '#E74C3C', // Rojo para tasas bajas
    },
  },
  aseguradoraCell: {
    textTransform: 'capitalize',
  },
  infoLegend: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  infoText: {
    fontSize: '0.875rem',
    color: '#64748b',
  },
  infoIcon: {
    color: '#02CBF4',
  },
}));

// Mock data for components not yet supported by the API
const mockData = {
  totalQuotes: 156,
  successfulQuotes: 98,
  failedQuotes: 58,
  successRate: 62.8,
  quotesByMonth: {
    Ene: 12,
    Feb: 15,
    Mar: 18,
    Abr: 22,
    May: 25,
    Jun: 30,
    Jul: 28,
    Ago: 32,
    Sep: 35,
    Oct: 40,
    Nov: 38,
    Dic: 42,
  },
  topAdvisors: {
    'Juan Pérez': 156,
    'María García': 142,
    'Carlos López': 128,
    'Ana Martínez': 115,
    'Pedro Sánchez': 98,
    'Laura Torres': 92,
    'Roberto Díaz': 85,
    'Sofia Ruiz': 78,
    'Miguel González': 72,
    'Carmen Vega': 68,
    'David Morales': 65,
    'Isabel Castro': 62,
    'José Ramírez': 58,
    'Elena Silva': 55,
    'Francisco Ortiz': 52,
  },
};

const Dashboard: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [metrics, setMetrics] = useState<IMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [dateError, setDateError] = useState<string>('');

  // Cargar datos iniciales solo una vez al montar el componente
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const data = await getMetrics();
        setMetrics(data);
        setError(null);
      } catch (err) {
        setError('Error al cargar las métricas');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = event.target.value;
    setStartDate(newStartDate);

    if (newStartDate && endDate && newStartDate > endDate) {
      setEndDate('');
    }
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = event.target.value;
    if (validateDates(startDate, newEndDate)) {
      setEndDate(newEndDate);
    }
  };

  const handleFilter = async () => {
    if (validateDates(startDate, endDate)) {
      await fetchMetrics();
    }
  };

  const validateDates = (newStartDate: string, newEndDate: string): boolean => {
    if (newStartDate && !newEndDate) {
      setDateError('Debes seleccionar una fecha final');
      return false;
    }
    if (newStartDate && newEndDate && newStartDate > newEndDate) {
      setDateError('La fecha inicial no puede ser posterior a la fecha final');
      return false;
    }
    setDateError('');
    return true;
  };

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const data = await getMetrics(startDate || undefined, endDate || undefined);
      setMetrics(data);
      setError(null);
    } catch (err) {
      setError('Error al cargar las métricas');
    } finally {
      setLoading(false);
    }
  };

  const getSuccessMetrics = () => {
    if (!metrics)
      return { successCount: 0, errorCount: 0, total: 0, successRate: 0, validationCount: 0 };

    const findCount = (estado: string) =>
      metrics.metrics.distribucionResultados.find((item) => item.estado === estado)?.cantidad || 0;

    const foundSuccessCount = findCount('SUCCESS');
    const foundErrorCount = findCount('ERROR');
    const foundValidationCount = findCount('DATA_ERROR');
    const foundTotal = foundSuccessCount + foundErrorCount;
    const foundSuccessRate = foundTotal > 0 ? (foundSuccessCount / foundTotal) * 100 : 0;

    return {
      successCount: foundSuccessCount,
      errorCount: foundErrorCount,
      total: foundTotal,
      successRate: Number(foundSuccessRate.toFixed(2)),
      validationCount: foundValidationCount,
    };
  };

  const { successCount, errorCount, total, successRate, validationCount } = getSuccessMetrics();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          font: {
            size: 12,
            family: "'Inter', sans-serif",
          },
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          family: "'Inter', sans-serif",
        },
        bodyFont: {
          size: 12,
          family: "'Inter', sans-serif",
        },
        displayColors: false,
        callbacks: {
          label(context: any) {
            return `${context.label}: ${context.raw}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif",
          },
          padding: 8,
        },
      },
      x: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif",
          },
          padding: 8,
        },
      },
    },
  };

  const formatMonth = (dateString: string) => {
    const [year, month] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });
  };

  const lineChartData = {
    labels: metrics?.metrics.cotizacionesPorMes.map((item) => formatMonth(item.mes)) || [],
    datasets: [
      {
        label: 'Cotizaciones por Mes',
        data: metrics?.metrics.cotizacionesPorMes.map((item) => item.totalCotizaciones) || [],
        borderColor: '#02CBF4',
        backgroundColor: 'rgba(2, 203, 244, 0.1)',
        tension: 0.3,
        fill: true,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: '#02CBF4',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
      },
    ],
  };

  const pieChartData = {
    labels: ['Exitosas', 'Fallidas', 'Rechazos por Políticas Aseguradoras'],
    datasets: [
      {
        data: [successCount, errorCount, validationCount],
        backgroundColor: ['#10B981', '#E74C3C', '#F59E0B'],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  const formatInsurerName = (name: string) => {
    // Capitalizar la primera letra y reemplazar guiones bajos por espacios
    return name
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const barChartData = {
    labels:
      metrics?.metrics.cotizacionesPorAseguradora.map((item) =>
        formatInsurerName(item.aseguradora)
      ) || [],
    datasets: [
      {
        label: 'Cotizaciones por Aseguradora',
        data: metrics?.metrics.cotizacionesPorAseguradora.map((item) => item.cantidad) || [],
        backgroundColor: [
          'rgba(2, 203, 244, 0.8)', // Azul corporativo
          'rgba(16, 185, 129, 0.8)', // Verde
          'rgba(245, 158, 11, 0.8)', // Naranja
          'rgba(139, 92, 246, 0.8)', // Púrpura
          'rgba(236, 72, 153, 0.8)', // Rosa
          'rgba(59, 130, 246, 0.8)', // Azul
          'rgba(249, 115, 22, 0.8)', // Naranja oscuro
          'rgba(16, 185, 129, 0.8)', // Verde
          'rgba(139, 92, 246, 0.8)', // Púrpura
          'rgba(236, 72, 153, 0.8)', // Rosa
          'rgba(59, 130, 246, 0.8)', // Azul
        ],
        borderColor: [
          '#02CBF4',
          '#10B981',
          '#F59E0B',
          '#8B5CF6',
          '#EC4899',
          '#3B82F6',
          '#F97316',
          '#10B981',
          '#8B5CF6',
          '#EC4899',
          '#3B82F6',
        ],
        borderWidth: 1,
        borderRadius: 8,
        hoverBackgroundColor: [
          'rgba(2, 203, 244, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(139, 92, 246, 1)',
          'rgba(236, 72, 153, 1)',
          'rgba(59, 130, 246, 1)',
          'rgba(249, 115, 22, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(139, 92, 246, 1)',
          'rgba(236, 72, 153, 1)',
          'rgba(59, 130, 246, 1)',
        ],
        hoverBorderColor: [
          '#02CBF4',
          '#10B981',
          '#F59E0B',
          '#8B5CF6',
          '#EC4899',
          '#3B82F6',
          '#F97316',
          '#10B981',
          '#8B5CF6',
          '#EC4899',
          '#3B82F6',
        ],
        hoverBorderWidth: 2,
        barThickness: 40,
      },
    ],
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif",
          },
          padding: 8,
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif",
          },
          padding: 8,
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  const formatCityName = (cityCode: string) => {
    const parts = cityCode.split('-');
    if (parts.length === 2) {
      const [departamento, ciudad] = parts;

      const capitalizeAndFormat = (str: string) => {
        return str
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .split(/[\s-_]+/)
          .map((word) => {
            if (word.toLowerCase() === 'dc' || word.toLowerCase() === 'd.c.') return 'D.C.';
            if (word.toLowerCase() === 'san') return 'San';
            if (word.toLowerCase() === 'santa') return 'Santa';
            if (word.toLowerCase() === 'del') return 'del';
            if (word.toLowerCase() === 'de') return 'de';
            if (word.toLowerCase() === 'la') return 'la';
            if (word.toLowerCase() === 'las') return 'las';
            if (word.toLowerCase() === 'los') return 'los';
            if (word.toLowerCase() === 'el') return 'el';

            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          })
          .join(' ');
      };

      const ciudadFormatted = capitalizeAndFormat(ciudad);
      const departamentoFormatted = capitalizeAndFormat(departamento);

      // Casos especiales para ciudades que no necesitan mostrar el departamento
      if (cityCode === 'bogota-bogota') {
        return 'Bogotá D.C.';
      }

      // Para otras ciudades, mostrar formato: "Ciudad - Departamento"
      return `${ciudadFormatted} - ${departamentoFormatted}`;
    }

    // Si no tiene el formato esperado, intentar capitalizar
    return cityCode.charAt(0).toUpperCase() + cityCode.slice(1).toLowerCase();
  };

  const horizontalBarData = {
    labels: Object.keys(mockData.topAdvisors),
    datasets: [
      {
        data: Object.values(mockData.topAdvisors),
        backgroundColor: Object.keys(mockData.topAdvisors).map((_, index) => {
          if (index === 0) return 'rgba(255, 215, 0, 0.8)'; // Oro
          if (index === 1) return 'rgba(192, 192, 192, 0.8)'; // Plata
          if (index === 2) return 'rgba(205, 127, 50, 0.8)'; // Bronce
          return 'rgba(2, 203, 244, 0.8)'; // Azul corporativo para el resto
        }),
        borderColor: Object.keys(mockData.topAdvisors).map((_, index) => {
          if (index === 0) return '#FFD700'; // Oro
          if (index === 1) return '#C0C0C0'; // Plata
          if (index === 2) return '#CD7F32'; // Bronce
          return '#02CBF4'; // Azul corporativo para el resto
        }),
        borderWidth: Object.keys(mockData.topAdvisors).map((_, index) => {
          if (index <= 2) return 2; // Borde más grueso para top 3
          return 1; // Borde normal para el resto
        }),
        borderRadius: 4,
        hoverBackgroundColor: Object.keys(mockData.topAdvisors).map((_, index) => {
          if (index === 0) return 'rgba(255, 215, 0, 1)'; // Oro
          if (index === 1) return 'rgba(192, 192, 192, 1)'; // Plata
          if (index === 2) return 'rgba(205, 127, 50, 1)'; // Bronce
          return 'rgba(2, 203, 244, 1)'; // Azul corporativo para el resto
        }),
        hoverBorderColor: Object.keys(mockData.topAdvisors).map((_, index) => {
          if (index === 0) return '#FFD700'; // Oro
          if (index === 1) return '#C0C0C0'; // Plata
          if (index === 2) return '#CD7F32'; // Bronce
          return '#02CBF4'; // Azul corporativo para el resto
        }),
        hoverBorderWidth: Object.keys(mockData.topAdvisors).map((_, index) => {
          if (index <= 2) return 3; // Borde más grueso en hover para top 3
          return 2; // Borde normal en hover para el resto
        }),
        barThickness: 20,
      },
    ],
  };

  const citiesBarChartData = {
    labels: metrics?.metrics.cotizacionesPorCiudad.map((item) => formatCityName(item.ciudad)) || [],
    datasets: [
      {
        label: 'Cotizaciones por Ciudad',
        data: metrics?.metrics.cotizacionesPorCiudad.map((item) => item.totalCotizaciones) || [],
        backgroundColor: 'rgba(2, 203, 244, 0.8)',
        borderColor: '#02CBF4',
        borderWidth: 1,
        borderRadius: 6,
        hoverBackgroundColor: 'rgba(2, 203, 244, 1)',
        hoverBorderColor: '#02CBF4',
        hoverBorderWidth: 2,
        barThickness: 20,
      },
    ],
  };

  const citiesBarChartOptions = {
    ...chartOptions,
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: true,
      mode: 'point' as const,
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif",
          },
          padding: 8,
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 10,
            family: "'Inter', sans-serif",
          },
          padding: 8,
          maxRotation: 0,
          stepSize: 1,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          family: "'Inter', sans-serif",
        },
        bodyFont: {
          size: 12,
          family: "'Inter', sans-serif",
        },
        displayColors: false,
        intersect: true,
        position: 'nearest' as const,
        callbacks: {
          title(context: any) {
            return context[0].label;
          },
          label(context: any) {
            return `Cotizaciones: ${context.raw.toLocaleString()}`;
          },
        },
      },
    },
  };

  const getSuccessRateClass = (rate: number) => {
    if (rate >= 80) return 'high';
    if (rate >= 60) return 'medium';
    return 'low';
  };

  const formatAseguradoraName = (name: string) => {
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  return (
    <div className={classes.root}>
      <Box className={classes.dateFilterContainer}>
        <Typography className={classes.filterTitle}>Filtro de Fechas</Typography>
        <Box className={classes.dateFilter}>
          <TextField
            className={classes.dateField}
            label="Fecha Inicio"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: endDate || new Date().toISOString().split('T')[0],
            }}
            variant="outlined"
            fullWidth
            error={!!dateError}
          />
          <TextField
            className={classes.dateField}
            label="Fecha Fin"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: startDate,
              max: new Date().toISOString().split('T')[0],
            }}
            variant="outlined"
            fullWidth
            error={!!dateError}
          />
        </Box>
        {dateError && <FormHelperText className={classes.errorText}>{dateError}</FormHelperText>}
        <Box className={classes.buttonContainer}>
          <Button
            className={classes.filterButton}
            onClick={handleFilter}
            disabled={!startDate || !endDate || !!dateError}>
            Filtrar
          </Button>
        </Box>
        <Box className={classes.infoLegend}>
          <InfoIcon className={classes.infoIcon} />
          <Typography className={classes.infoText}>
            Por defecto, todas las gráficas muestran los datos del último mes desde el día actual,
            excepto la gráfica de Tendencia de Cotizaciones Generales que muestra los datos del año
            actual.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* KPI Cards */}
        <Grid item xs={12} className={classes.kpiGrid}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6} md={2}>
              <Card className={`${classes.card} ${classes.kpiCard}`}>
                <CardContent className={classes.cardContent}>
                  <Typography className={classes.title}>
                    Total Cotizaciones Por Aseguradoras
                  </Typography>
                  <div className={classes.kpiValue}>
                    {loading ? (
                      <CircularProgress size={24} className={classes.kpiLoading} />
                    ) : (
                      <Typography className={classes.value}>{total}</Typography>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card className={`${classes.card} ${classes.kpiCard}`}>
                <CardContent className={classes.cardContent}>
                  <Typography className={classes.title}>
                    Cotizaciones Exitosas Por Aseguradoras
                  </Typography>
                  <div className={classes.kpiValue}>
                    {loading ? (
                      <CircularProgress size={24} className={classes.kpiLoading} />
                    ) : (
                      <Typography className={classes.value}>{successCount}</Typography>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card className={`${classes.card} ${classes.kpiCard}`}>
                <CardContent className={classes.cardContent}>
                  <Typography className={classes.title}>
                    Cotizaciones Fallidas Por Aseguradoras
                  </Typography>
                  <div className={classes.kpiValue}>
                    {loading ? (
                      <CircularProgress size={24} className={classes.kpiLoading} />
                    ) : (
                      <Typography className={classes.value}>{errorCount}</Typography>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card className={`${classes.card} ${classes.kpiCard}`}>
                <CardContent className={classes.cardContent}>
                  <Typography className={classes.title}>Tasa de Éxito</Typography>
                  <div className={classes.kpiValue}>
                    {loading ? (
                      <CircularProgress size={24} className={classes.kpiLoading} />
                    ) : (
                      <Typography className={classes.value}>{successRate}%</Typography>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card className={`${classes.card} ${classes.kpiCard}`}>
                <CardContent className={classes.cardContent}>
                  <Typography className={classes.title}>Tasa de Éxito Ajustada</Typography>
                  <div className={classes.kpiValue}>
                    {loading ? (
                      <CircularProgress size={24} className={classes.kpiLoading} />
                    ) : (
                      <Typography className={classes.value}>
                        {successCount + errorCount + validationCount > 0
                          ? (
                              ((successCount + validationCount) /
                                (successCount + errorCount + validationCount)) *
                              100
                            ).toFixed(2)
                          : 0}
                        %
                      </Typography>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card className={`${classes.card} ${classes.kpiCard}`}>
                <CardContent className={classes.cardContent}>
                  <Typography className={classes.title}>
                    Rechazos por Políticas Aseguradoras
                  </Typography>
                  <div className={classes.kpiValue}>
                    {loading ? (
                      <CircularProgress size={24} className={classes.kpiLoading} />
                    ) : (
                      <Typography className={classes.value}>{validationCount}</Typography>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid item xs={12} md={8}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography className={classes.chartTitle}>
                Tendencia de Cotizaciones Generales
              </Typography>
              <div className={classes.chartContainer}>
                {loading ? (
                  <div className={classes.loadingContainer}>
                    <CircularProgress />
                  </div>
                ) : error ? (
                  <Typography className={classes.errorMessage}>{error}</Typography>
                ) : (
                  <Line data={lineChartData} options={chartOptions} />
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography className={classes.chartTitle}>Distribución de Resultados</Typography>
              <div className={classes.chartContainer}>
                {loading ? (
                  <div className={classes.loadingContainer}>
                    <CircularProgress />
                  </div>
                ) : error ? (
                  <Typography className={classes.errorMessage}>{error}</Typography>
                ) : (
                  <Pie data={pieChartData} options={chartOptions} />
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* Cotizaciones por Aseguradora */}
        <Grid item xs={12}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography className={classes.chartTitle}>
                Cotizaciones Exitosas por Aseguradora
              </Typography>
              <div className={classes.chartContainer}>
                {loading ? (
                  <div className={classes.loadingContainer}>
                    <CircularProgress />
                  </div>
                ) : error ? (
                  <Typography className={classes.errorMessage}>{error}</Typography>
                ) : (
                  <Bar data={barChartData} options={barChartOptions} />
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* Cotizaciones por Ciudad */}
        <Grid item xs={12}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography className={classes.chartTitle}>
                Top 30 Ciudades por Cotizaciones
              </Typography>
              <div style={{ height: '600px', marginTop: '16px', position: 'relative' }}>
                {loading ? (
                  <div className={classes.loadingContainer}>
                    <CircularProgress />
                  </div>
                ) : error ? (
                  <Typography className={classes.errorMessage}>{error}</Typography>
                ) : (
                  <Bar data={citiesBarChartData} options={citiesBarChartOptions} />
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* Métricas por Aseguradora */}
        <Grid item xs={12}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography className={classes.chartTitle}>
                Detalle de Cotizaciones por Aseguradoras
              </Typography>
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} aria-label="metrics table">
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableHeaderCell}>Aseguradora</TableCell>
                      <TableCell className={classes.tableHeaderCell} align="right">
                        Total
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} align="right">
                        Exitosas
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} align="right">
                        Fallidas
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} align="right">
                        Rechazos por Políticas Aseguradoras
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} align="right">
                        Tasa de Éxito
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell} align="right">
                        Tasa de Éxito Ajustada (Exitosas + Rechazos por Políticas)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" className={classes.tableCell}>
                          <CircularProgress size={24} className={classes.kpiLoading} />
                        </TableCell>
                      </TableRow>
                    ) : error ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" className={classes.tableCell}>
                          <Typography color="error">{error}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      metrics?.metrics.metricasAseguradoras
                        .sort((a, b) => b.successRate - a.successRate)
                        .map((aseguradora) => (
                          <TableRow key={aseguradora.aseguradora} className={classes.tableRow}>
                            <TableCell
                              className={`${classes.tableCell} ${classes.aseguradoraCell}`}>
                              {formatAseguradoraName(aseguradora.aseguradora)}
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              {aseguradora.totalCount.toLocaleString()}
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              {aseguradora.successCount.toLocaleString()}
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              {aseguradora.errorCount.toLocaleString()}
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              {aseguradora.dataErrorCount.toLocaleString()}
                            </TableCell>
                            <TableCell
                              className={`${classes.tableCell} ${
                                classes.successRateCell
                              } ${getSuccessRateClass(aseguradora.successRate)}`}
                              align="right">
                              {aseguradora.successRate.toFixed(2)}%
                            </TableCell>
                            <TableCell
                              className={`${classes.tableCell} ${
                                classes.successRateCell
                              } ${getSuccessRateClass(aseguradora.adjustedSuccessRate)}`}
                              align="right">
                              {aseguradora.adjustedSuccessRate.toFixed(2)}%
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top 15 Asesores */}
        <Grid item xs={12}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography className={classes.chartTitle}>
                Top 15 Asesores por Cotizaciones
              </Typography>
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} aria-label="top advisors table">
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableHeaderCell}>Posición</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Asesor</TableCell>
                      <TableCell className={classes.tableHeaderCell} align="right">
                        Cotizaciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" className={classes.tableCell}>
                          <CircularProgress size={24} className={classes.kpiLoading} />
                        </TableCell>
                      </TableRow>
                    ) : error ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" className={classes.tableCell}>
                          <Typography color="error">{error}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      metrics?.metrics.topAsesores.map((asesor, index) => (
                        <TableRow key={asesor.email} className={classes.tableRow}>
                          <TableCell className={`${classes.tableCell} ${classes.rankCell}`}>
                            <span
                              className={`${classes.rankBadge} ${
                                index === 0
                                  ? classes.goldBadge
                                  : index === 1
                                  ? classes.silverBadge
                                  : index === 2
                                  ? classes.bronzeBadge
                                  : classes.defaultBadge
                              }`}>
                              {index + 1}
                            </span>
                          </TableCell>
                          <TableCell className={classes.tableCell}>{asesor.nombre}</TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            {asesor.totalCotizaciones}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default Dashboard;
